import ExtendedPath from "csi/core/ExtendedPath";
import csi from "csi/main";
import ReflectionAttribute from "csi/reflection/ReflectionAttribute";
import ReflectionClass from "csi/reflection/ReflectionClass";
import { ATTR_IMAGES, DEFAULT_IMAGE_LABEL, getClassAttribute } from "csi/reflection/reflection";
import reflectionHints from "csi/reflectionHints";
import { isDefined, isNullOrUndefined } from "csi/utility/coreUtility";
import { FORMAT_DATETIME } from "csi/utility/dateUtility";
import { PATH_OP_INDEX, pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import { each, omitBy } from "lodash";
import { FORMAT_TYPE } from "src/c8-platform/type/format";
import { ATTR_NAME, ATTR_TYPE, C8AttributeId, C8NodeType } from "src/c8-platform/type/node";
import { C8AttributeKey, C8AttributeType, VALUE_TYPE, isContainerValueType, isRefValueType } from "src/c8-platform/type/value";
import { parseExtendedPath } from "./extendedPathService";
import { IKey } from "./types";
import { AttributeGroup } from "csi/reflection/reflectionTypes";

/**
 * The string representation of a key is as follows:
 *	(___name|___prop|id)(/index):path:keyRel(:owner)({matrix})
 *
 * As of 7.9.1, owner is no longer present, and keyRel will always be 0 in new keys;
 * eventually it will be completely removed
 */

interface IFullKey {
	dpkey: C8AttributeKey;
}

const RE_IMAGES_KEY = /^___Images_/;
const RE_IMAGES_NAME = /^Images_/;
const GROUP_BY_REF_NAME = "_GroupByRef_";
const GROUP_PREFIX = "Group_";
const IMAGES_PREFIX = "Images_";
const NON_ID_PREFIX = "___";
const PROPS = [xml.PROP_CREATE_TIME, xml.PROP_CREATOR];

function _addClassKeys(
	rc: ReflectionClass,
	extendedPath: ExtendedPath,
	keys: Key[],
	omitMap?: Record<C8AttributeId, number>,
): void {
	rc.getAttributes(true, true).forEach((rca) => {
		const { attributeId: id, isHidden, isSecondary } = rca;

		if (!omitMap?.[id] && rca.isEnabled && !isHidden && !isSecondary) {
			keys.push(new Key({ extendedPath, id }));
		}
	});
}

/**
 * Separate key into components
 */
function parseString(s: C8AttributeKey, forMigration?: boolean): IKey | undefined {
	let n = s.indexOf(":");
	const key: IKey = {};

	// error if missing any id, prop, or name
	if (!n) {
		return;
	}

	// extract path/keyRel/owner/matrix, but parse later so rest of properties are known
	const suffix = s.substring(n + 1);

	// extract id, index, name, and prop
	s = s.substring(0, n);

	if (s.indexOf(NON_ID_PREFIX) == 0) {
		// name or prop - check to see if known property
		s = s.substring(3);

		if (xml.propertyType(s)) {
			key.prop = s;
		} else {
			key.name = s;

			// assign attribute id if this is custom images key
			if (RE_IMAGES_NAME.test(s)) {
				key.id = s.replace(RE_IMAGES_NAME, "");
			}
		}
	} else {
		// name or id with optional index - assign to id even though it is ambiguous which one it is
		n = s.indexOf("/");

		if (n > 0) {
			key.id = s.substring(0, n);
			key.index = s.substring(n + 1);
		} else if (n == 0) {
			return;
		} else {
			key.id = s;
		}
	}

	try {
		return parseSuffix(suffix, key, forMigration);
	} catch (ex) {
		// key not formatted correctly
	}
}

/**
 * Parse suffix into components
 */
function parseSuffix(s: string, key: IKey, forMigration?: boolean): IKey | undefined {
	const n = s.indexOf("{");

	if (n > 0) {
		key.matrix = s.substring(n + 1, s.length - 1);
		s = s.substring(0, n);
	}

	// split by ":" to determine owner, keyRel and path
	const a = s.split(":");
	if (a.length < 2) {
		return;
	}

	// parse owner and keyRel
	s = a.pop();
	const m = parseInt(s);

	if (String(m) === s) {
		key.keyRel = m;
	} else {
		// found old key with owner - track and continue
		key.originalOwner = s;
		s = a.pop();
		key.keyRel = parseInt(s);

		if (String(key.keyRel) !== s) {
			return;
		}
	}

	// join path back together with remaining parts
	const path = a.join(":");
	if (path.includes("(")) {
		try {
			const ep = parseExtendedPath(path);
			if (ep.startClass) {
				key.extendedPath = ep;

				if (key.id &&
					key.originalOwner &&
					(key.originalOwner !== ep.endType) &&
					!ep.endClass.getAttribute(key.id, true, false, true)
				) {
					ep.setEndType(key.originalOwner);
				}

				if (ep.value !== path) {
					key.originalTypedPath = path;
				}
			}

			key.path = ep.standardPath;
		} catch (ex) {
			const ep = parseExtendedPath(path, null, true);
			if (ep) {
				if (forMigration && ep.startType) {
					key.extendedPath = ep;
					key.invalidPath = true;
				}

				key.path = ep.standardPath;
			} else {
				return;
			}
		}
	} else {
		key.path = path;
	}

	return key;
}

class Key implements IKey {
	/** name used for special grouping key */
	static readonly GROUP_BY_REF_NAME = GROUP_BY_REF_NAME;

	/** deprecated keyRel for attributes referenced by column filters */
	static readonly INDEPENDENT_INDEX = -3;

	/** convert Key object to typed/untyped string key */
	static fromKey(key: Key | C8AttributeKey): C8AttributeKey {
		return key instanceof Key ? key.toString() : key;
	}

	/**
	 * Return list of keys for given extended path and Node properties (CR, CT) if desired;
	 * filter out those that are disabled or hidden
	 * @param bDomain - if true, then add keys for all attributes in domain referenced by extended path
	 */
	static getExtendedPathKeys(extendedPath: ExtendedPath, bNodeProperties?: boolean, bDomain?: boolean): Key[] {
		const rc = extendedPath.getEndClass();
		const { domainClass, type } = rc;
		const bSecondary = !!domainClass && (domainClass.primary != rc);
		const secondaries = domainClass?.secondaries;
		const secondaryOmitMap: Record<C8AttributeId, number> = {};
		const keys = [];

		// if adding attributes from domain, then omit certain secondary attributes
		if (bSecondary || bDomain) {
			secondaryOmitMap[xml.ATTR_NAME] = 1;
			secondaryOmitMap[xml.ATTR_PARENT] = 1;
			csi.hashArray(reflectionHints.getPrivateSecondaryAttributes(type), null, Boolean, secondaryOmitMap);
		}

		_addClassKeys(rc, extendedPath, keys, bSecondary ? secondaryOmitMap : null);

		if (bDomain) {
			if (bSecondary) {
				// add path step to get to primary class from secondary class
				extendedPath = extendedPath.clone();
				extendedPath.addRelationStep(xml.ATTR_PARENT, domainClass.type);

				// add keys for primary class
				_addClassKeys(domainClass.primary, extendedPath, keys);
			}

			// add keys from secondary classes
			each(secondaries, (rcSecondary, idRelation) => {
				// skip if started on this secondary class
				if (rc != rcSecondary) {
					const epSecondary = extendedPath.clone();
					epSecondary.addRelationStep(idRelation, rcSecondary.getType());
					_addClassKeys(rcSecondary, epSecondary, keys, secondaryOmitMap);
				}
			});
		}

		if (bNodeProperties) {
			PROPS.forEach((prop) => {
				keys.push(new Key({ extendedPath, prop }));
			});
		}

		return keys;
	}

	/**
	 * Return key representing an attribute group of form "___Group_<groupName>:<groupPath>:0"
	 */
	static getAttributeGroupKey(groupName: string, groupPath: ExtendedPath, matrix?: string): Key {
		return new Key({
			extendedPath: groupPath,
			matrix,
			name: `${GROUP_PREFIX}${groupName}`,
		});
	}

	/**
	 * Return list of keys for domain class attributes and Node properties (CR, CT) if desired;
	 * filter out those that are disabled or hidden
	 */
	static getKeysForType(typeNode: C8NodeType, bNodeProperties?: boolean): Key[] {
		const ep = new ExtendedPath(typeNode);
		return Key.getExtendedPathKeys(ep, bNodeProperties, true);
	}

	/**
	 * Return key used to uniquely identify filters for the given matrix key
	 */
	static getMatrixConfigurableFilterKey(components: IKey): C8AttributeKey {
		const key = new Key({
			...components,
			keyRel: null,
		});

		return key.toString();
	}

	/**
	 * Return unique string for given path and optional matrix
	 */
	static getPathAndMatrixId(extendedPath: ExtendedPath, matrix?: string): string {
		const matrixKey = matrix ? `{${matrix}}` : "";
		return `${extendedPath.value}${matrixKey}`;
	}

	/** return attribute key for model attribute given a key that could be a referenced image key */
	static referencedImagesBaseKey(key: C8AttributeKey | Key): C8AttributeKey | undefined {
		return Key.toKey(key).referencedImagesBaseKey;
	}

	/** convert key to Key object (if not one already) */
	static toKey(key: Key | C8AttributeKey): Key {
		return key instanceof Key ? key : new Key({ dpkey: key });
	}

	/** return key without type annotations in path */
	static untypedKey(key: Key | C8AttributeKey): C8AttributeKey {
		return Key.toKey(key).untypedString;
	}

	/** return key composed from parts without type annotations in path */
	static untypedKeyFromParts(params: IKey): C8AttributeKey {
		return new Key(params).untypedString;
	}

	/** extended path */
	readonly extendedPath?: ExtendedPath;

	/** key for heading with same properties (same as untypedString, but without ":0") */
	readonly headingKey?: string;

	/** attribute id (name will override id) */
	readonly id?: C8AttributeId;

	/** key for container attributes */
	readonly index?: number | string;

	/** used for migration to indicate typed path does not exist in the model */
	readonly invalidPath?: boolean;

	/** index/key of relationship to which key applies (is null when used for def.getHeadingKey) */
	readonly keyRel: number | null = 0;

	/** matrix id */
	readonly matrix?: string;

	/** heading name */
	readonly name?: string;

	/** track original key passed - could be different from resulting one due to migration from one format to another */
	readonly originalKey: C8AttributeKey;

	/** track original owner parsed from key - indicates attribute owner class which could previously differ from end type of path; no longer part of key */
	readonly originalOwner?: C8NodeType;

	/** track original key's typed path if different from the resulting path */
	readonly originalTypedPath?: string;

	/** untyped path */
	readonly path: string;

	/** node property */
	readonly prop?: string;

	/** typed key */
	readonly typedString?: C8AttributeKey;

	/** untyped key */
	readonly untypedString?: C8AttributeKey;

	constructor(params: IKey | IFullKey, forMigration?: boolean) {
		const { dpkey } = params as IFullKey;
		let keyParams: IKey;

		if (dpkey) {
			keyParams = parseString(dpkey, forMigration);
			this.originalKey = dpkey;
		} else {
			keyParams = params as IKey;
		}

		if (keyParams) {
			Object.assign(this, keyParams);
			this.path ||= this.extendedPath?.standardPath ?? "";
			this.headingKey = this.#composeString(this.path);
			this.typedString = this.#getTypedString();
			this.untypedString = this.#composeString(this.path, this.keyRel);
		}
	}

	/**
	 * Return string representation of key
	 */
	toString(): C8AttributeKey {
		return this.typedString || this.untypedString || "";
	}

	/**
	 * Generate new Key from this one with one or more changed properties
	 */
	copy(params?: IKey): Key {
		const {
			id,
			index,
			keyRel,
			matrix,
			name,
			prop,
		} = this;

		let {
			extendedPath,
			path,
		} = this;

		// optimize assigning of paths - if a new extended path is being provided, then no reason to clone existing path first
		const newExtendedPath = params?.extendedPath;

		if (newExtendedPath) {
			path = newExtendedPath.getStandardPath();
			extendedPath = null;
		} else {
			extendedPath = extendedPath?.clone();
		}

		return new Key({
			extendedPath,
			id,
			index,
			keyRel,
			matrix,
			name,
			path,
			prop,
			...params,
		});
	}

	/**
	 * Return attribute group referenced by key if it represents such a group
	 */
	get attributeGroup(): AttributeGroup | undefined {
		const { extendedPath, isGroupKey, name } = this;
		
		if (isGroupKey) {
			const groupName = name.substring(GROUP_PREFIX.length);
			return extendedPath.endClass?.attributeGroups.find((group) => group.name === groupName);
		}
	}

	/**
	 * Return true if key represents attribute that is disabled
	 */
	get attributeIsDisabled(): boolean {
		const { reflectionAttribute } = this;
		return reflectionAttribute && !reflectionAttribute.isEnabled;
	}

	/**
	 * Return matrix group id for key if it represents a matrix group
	 */
	get candidateMatrixGroupId(): string | undefined {
		return this.name || this.id;
	}

	/**
	 * Return properties from key that are configured as part of heading
	 */
	get components(): IKey {
		const { id, index, matrix, path, prop, name } = this;
		return omitBy({ id, index, matrix, path, prop, name }, isNullOrUndefined);
	}

	/**
	 * Return domain type at end of the path if it has extended path
	 */
	get domainType(): C8NodeType | undefined {
		return this.extendedPath?.endDomainType;
	}

	/**
	 * Return extended path plus additional step along attribute if present
	 */
	get extendedPathThroughAttribute(): ExtendedPath {
		const { extendedPath, id, index, reflectionAttribute } = this;

		if (reflectionAttribute && isRefValueType(reflectionAttribute.valueType)) {
			const { standardPath } = extendedPath;
			let path = pathCreate(id);

			if (isDefined(index)) {
				path = pathCreate(path, PATH_OP_INDEX, String(index));
			}

			try {
				return extendedPath.plus(path);
			} catch (ex) {
				// in some cases there is not one single typed path (multiple targets), so use untyped path instead
				return ExtendedPath.createUntypedPath(pathCreate(standardPath, path));
			}
		}

		return extendedPath;
	}

	/**
	 * Return Key used to show referenced images of this key, if it represents a relationship that targets a class that is an image container 
	 */
	get imagesKey(): Key | undefined {
		const { id, reflectionAttribute, reflectionAttributeDestination } = this;

		if (isContainerValueType(reflectionAttribute?.valueType) && reflectionAttributeDestination?.isImageContainer) {
			return this.copy({
				name: `${IMAGES_PREFIX}${id}`,
			});
		}
	}

	/**
	 * Return true if key references the default image of some class
	 */
	get isDefaultImage(): boolean {
		const { id, index } = this;
		return id === ATTR_IMAGES && (isNullOrUndefined(index) || (index === DEFAULT_IMAGE_LABEL));
	}

	/**
	 * Return true if key uses special GroupByRef name that can appear as key in group settings
	 */
	get isGroupByRefKey(): boolean {
		return this.name === GROUP_BY_REF_NAME;
	}

	/**
	 * Return true if key references an attribute group 
	 */
	get isGroupKey(): boolean {
		return !!this.name?.startsWith(GROUP_PREFIX);
	}	

	/**
	 * Return true if key has a typed string
	 */
	get isTyped(): boolean {
		return !!this.typedString;
	}

	/**
	 * Return new instance of key along given path
	 */
	getKeyAlongPath(prefixPath: ExtendedPath): Key {
		const extendedPath = prefixPath.clone().append(this.extendedPath);
		return this.copy({ extendedPath });
	}

	/**
	 * Return new instance of key relative to the given prefix path
	*/
	getKeyRelativeToPath(prefixPath: ExtendedPath): Key {
		const extendedPath = this.extendedPath.relativeTo(prefixPath);
		return this.copy({ extendedPath });
	}

	/**
	 * Return first part of key which takes into consideration the name, attribute id, node property, and index
	 */
	get prefix(): string {
		const { id, index, isDefaultImage, name, prop } = this;
		const needPrefix = name || prop;
		const part1 = needPrefix ? (NON_ID_PREFIX + needPrefix) : (id || "");
		const part2 = (!prop && !isDefaultImage && isDefined(index)) ? `/${index}` : "";
		return part1 + part2;
	}

	/**
	 * Return true if key is for showing the default image of each reference in attribute (only for ref-container attributes) 
	 */
	get isForShowingReferencedImages(): boolean {
		const { id, name } = this;
		return name === `${IMAGES_PREFIX}${id}`;
	}

	/**
	 * Return true if key is showing a labeled image (other than the default) 
	 */
	get isImageLabel(): boolean {
		const { id, index } = this;
		return (id === ATTR_IMAGES) && isDefined(index);
	}

	/**
	 * Return true if key represents node name on current item (no path)
	 */
	get isNameWithoutPath(): boolean {
		return !this.path && ((this.id === xml.ATTR_NAME) || (this.prop === xml.PROP_NAME));
	}

	/**
	 * Return attribute key for model attribute if this is a key for a list of referenced images
	 */
	get referencedImagesBaseKey(): C8AttributeKey | undefined {
		if (this.isForShowingReferencedImages) {
			return this.typedString.replace(RE_IMAGES_KEY, "");
		}
	}

	/**
	 * Get reflection attribute
	 */
	get reflectionAttribute(): ReflectionAttribute | null {
		return getClassAttribute(this.extendedPath?.endClass, this.id);
	}

	/**
	 * Get class referenced by attribute when it is a relationship
	 */
	get reflectionAttributeDestination(): ReflectionClass | undefined {
		return this.reflectionAttribute?.getDestinationClass(this.extendedPath?.endClass);
	}

	/**
	 * Return value format for key if known
	 */
	get valueFormat(): string | undefined {
		const { extendedPath, id, prop, reflectionAttribute } = this;

		if (id === ATTR_TYPE) {
			return FORMAT_TYPE;
		} else if (id) {
			return reflectionAttribute?.format;
		} else if (prop === xml.PROP_CREATE_TIME) {
			return FORMAT_DATETIME;
		}

		return extendedPath?.endRelation?.format;
	}

	/**
	 * Return value type for key if known
	 */
	get valueType(): C8AttributeType | undefined {
		const { extendedPath, id, prop, reflectionAttribute } = this;

		if ((id === ATTR_NAME) || (id === ATTR_TYPE)) {
			return VALUE_TYPE.STRING;
		} else if (id) {
			return reflectionAttribute?.valueType;
		} else if (prop) {
			return xml.propertyType(prop);
		}

		return extendedPath?.endRelation?.valueType;
	}

	/**
	 * Compose DataPoint key from components
	 */
	#composeString(path: string, keyRel?: number): C8AttributeKey | undefined {
		const { matrix, prefix } = this;
		if (prefix) {
			return `${prefix}:${path}${isDefined(keyRel) ? ":0" : ""}${matrix ? `{${matrix}}` : ""}`;
		}
	}

	/**
	 * Return typed string for key
	 */
	#getTypedString(): C8AttributeKey | undefined {
		const { extendedPath, keyRel } = this;

		if (extendedPath?.startClass) {
			return this.#composeString(extendedPath.value, keyRel);
		}
	}
}

export = Key;